import { useId } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { CampaignInfluencer } from '@/marketing/models/campaignInfluencer';
import {
  CREATOR_SCORE_TIER_LABELS,
  formatCreatorScore,
  getCreatorScoreTier,
  isBelowMinimumScore,
  MINIMUM_QUALIFYING_SCORE,
  SCORE_REVIEW_LABELS,
} from '@/marketing/models/creatorScore';
import { useCreatorScores } from './CreatorScoreProvider';

export function CreatorScoreBadge({
  creator,
  highlightLowScore = true,
  storedScore,
}: {
  creator: CampaignInfluencer;
  highlightLowScore?: boolean;
  storedScore?: { finalScore: number | null; reviewStatus: string; freshness: string; inputCutoff: string } | null;
}) {
  const tooltipId = useId();
  const { scores, isLoading, isError, openScore } = useCreatorScores();
  const linked = creator.userId != null;
  const score =
    storedScore === undefined
      ? creator.userId == null
        ? undefined
        : scores[creator.userId]
      : (storedScore ?? undefined);
  const loading = storedScore === undefined && linked && isLoading;
  const failed = storedScore === undefined && linked && isError;
  const blocked = !loading && !failed && score?.reviewStatus === 'blocked';
  const tone = blocked
    ? 'tw-text-red-700 dark:tw-text-red-300'
    : highlightLowScore && !loading && !failed && isBelowMinimumScore(score?.finalScore)
      ? 'tw-text-amber-700 dark:tw-text-amber-400'
      : '';
  const reason =
    !linked && storedScore === undefined
      ? 'Hub user not linked'
      : loading
        ? 'Loading score…'
        : failed
          ? 'Score unavailable'
          : score?.finalScore == null
            ? 'No stored numeric score'
            : undefined;

  return (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus']}
      overlay={
        <Tooltip id={`creator-score-${tooltipId}`}>
          <div className="tw-text-left">
            {reason && <div>{reason}</div>}
            {score && !loading && !failed && (
              <>
                <div>Score: {formatCreatorScore(score.finalScore)}</div>
                <div>
                  {CREATOR_SCORE_TIER_LABELS[
                    getCreatorScoreTier(score.finalScore, score.reviewStatus as keyof typeof SCORE_REVIEW_LABELS)
                  ]}{' '}
                  · minimum qualifying {MINIMUM_QUALIFYING_SCORE.toFixed(1)}
                </div>
                <div>
                  Review:{' '}
                  {SCORE_REVIEW_LABELS[score.reviewStatus as keyof typeof SCORE_REVIEW_LABELS] ?? score.reviewStatus}
                </div>
                {blocked && <div>Open details for the recorded reason.</div>}
                <div>Snapshot: {score.freshness}</div>
                <div>Data through: {score.inputCutoff.slice(0, 10)}</div>
              </>
            )}
            <div className="tw-mt-1">Click for score breakdown and reasons.</div>
          </div>
        </Tooltip>
      }
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          openScore(creator);
        }}
        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-font-semibold tw-tabular-nums hover:tw-bg-muted focus-visible:tw-outline focus-visible:tw-outline-2 ${tone}`}
        aria-label={`View score details for ${creator.handle ?? creator.influencerId}`}
        aria-description={`${blocked ? 'Blocked. ' : ''}${reason ?? `Score: ${formatCreatorScore(score?.finalScore)}`}`}
        aria-busy={loading}
      >
        <span>{loading ? '…' : failed ? '!' : (score?.finalScore?.toFixed(1) ?? '—')}</span>
        {blocked && (
          <span className="tw-rounded tw-bg-red-50 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold dark:tw-bg-red-950">
            Blocked
          </span>
        )}
      </button>
    </OverlayTrigger>
  );
}
